/**
 * Editor de texto de la noticia (Tiptap).
 *
 * Deliberadamente corto de funciones: encabezados, negrita, cursiva, listas,
 * citas y enlaces. Es lo que el §10 pide y es lo que sobrevive al viaje a
 * WordPress — un editor con colores, tipografías y columnas produciría HTML que
 * el tema de cada periódico va a ignorar o, peor, a romper.
 *
 * Lo que sale de aquí **se vuelve a sanear en el servidor** (`sanitizeArticleHtml`).
 * Que Tiptap normalice lo que se pega no es garantía: cualquiera puede llamar a
 * la API sin pasar por esta pantalla.
 */

import { Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';

export interface EditorProps {
  value: string;
  onChange: (html: string) => void;
  /** Se usa para asociar la etiqueta con el área de edición. */
  id?: string;
}

interface ToolButton {
  label: string;
  title: string;
  isActive: () => boolean;
  run: () => void;
}

export function ArticleEditor({ value, onChange, id = 'cuerpo' }: EditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // El título de la noticia es su propio campo. Permitir un h1 dentro del
        // cuerpo produce dos títulos en la página del periódico, que además es
        // un defecto de SEO y de accesibilidad.
        heading: { levels: [2, 3, 4] },

        /**
         * El enlace se configura **aquí** y no como extensión aparte.
         *
         * StarterKit 3 ya trae `link`; añadir `@tiptap/extension-link` por
         * separado registra la misma extensión dos veces, y Tiptap avisa de
         * duplicado y se queda con una de las dos configuraciones — la que gane
         * depende del orden, que es exactamente el tipo de cosa que funciona en
         * desarrollo y falla en producción.
         */
        link: {
          openOnClick: false,
          // Sin esto, pegar texto con un enlace `javascript:` lo dejaría en el
          // documento. El servidor lo quitaría igual, pero es mejor que el
          // editor no lo acepte: así el redactor lo ve al momento.
          protocols: ['http', 'https', 'mailto'],
          HTMLAttributes: { rel: 'noopener noreferrer' },
        },
      }),

      // Sin esto el área de edición aparece en blanco y no queda claro que se
      // puede escribir ahí. El texto lo pinta el CSS con `is-editor-empty`.
      Placeholder.configure({ placeholder: 'Escribe la noticia…' }),
    ],
    content: value,
    editorProps: {
      attributes: {
        id,
        class:
          'prose-editor min-h-[20rem] w-full rounded-lg border border-[var(--color-line-strong)] bg-[var(--color-card)] px-4 py-3 text-[0.95rem] leading-relaxed outline-none transition focus:border-[var(--color-brand)] focus:ring-3 focus:ring-[var(--color-brand-soft)]',
      },
    },
    onUpdate: ({ editor: instance }) => {
      const html = instance.getHTML();
      // Tiptap deja este marcador cuando el documento está vacío. Guardarlo
      // haría que una noticia "vacía" pasara la validación de contenido del
      // servidor y saliera con un párrafo en blanco.
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  /**
   * Sincroniza cuando el valor cambia **desde fuera**: al cargar un artículo
   * existente, por ejemplo.
   *
   * La comparación con `getHTML()` no es una optimización, es lo que evita un
   * bucle: sin ella, cada pulsación dispara `onChange`, el padre reenvía el
   * mismo valor, esto llama a `setContent`, y el cursor salta al principio.
   */
  useEffect(() => {
    if (editor && value !== editor.getHTML() && value !== '') {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  if (!editor) {
    return (
      <div className="min-h-[18rem] rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)]" />
    );
  }

  const buttons: ToolButton[] = [
    {
      label: 'Negrita',
      title: 'Negrita (Ctrl+B)',
      isActive: () => editor.isActive('bold'),
      run: () => editor.chain().focus().toggleBold().run(),
    },
    {
      label: 'Cursiva',
      title: 'Cursiva (Ctrl+I)',
      isActive: () => editor.isActive('italic'),
      run: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      label: 'H2',
      title: 'Subtítulo',
      isActive: () => editor.isActive('heading', { level: 2 }),
      run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      label: 'H3',
      title: 'Subtítulo menor',
      isActive: () => editor.isActive('heading', { level: 3 }),
      run: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    {
      label: 'Lista',
      title: 'Lista con puntos',
      isActive: () => editor.isActive('bulletList'),
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      label: 'Numerada',
      title: 'Lista numerada',
      isActive: () => editor.isActive('orderedList'),
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      label: 'Cita',
      title: 'Cita',
      isActive: () => editor.isActive('blockquote'),
      run: () => editor.chain().focus().toggleBlockquote().run(),
    },
    {
      label: 'Enlace',
      title: 'Añadir o quitar enlace',
      isActive: () => editor.isActive('link'),
      run: () => {
        if (editor.isActive('link')) {
          editor.chain().focus().unsetLink().run();
          return;
        }

        const url = window.prompt('Dirección del enlace');

        if (!url) {
          return;
        }

        editor.chain().focus().setLink({ href: url }).run();
      },
    },
  ];

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-1" role="toolbar" aria-label="Formato del texto">
        {buttons.map((button) => (
          <button
            key={button.label}
            type="button"
            title={button.title}
            // `aria-pressed` es lo que le dice a un lector de pantalla si la
            // negrita está puesta. Sin esto, quien no ve el resaltado no lo sabe.
            aria-pressed={button.isActive()}
            onClick={button.run}
            className={`rounded-md border px-2 py-1 text-xs transition ${
              button.isActive()
                ? 'border-[var(--color-brand)] bg-[var(--color-brand)] text-white'
                : 'border-[var(--color-line)] hover:bg-[var(--color-surface)]'
            }`}
          >
            {button.label}
          </button>
        ))}
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
